'use client';

import { useEffect, useRef, useState } from 'react';
import { MARKS } from '@contentful/rich-text-types';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import { DisplayHeadline } from 'components/fall-2026/modules/RichTextHeadline/DisplayHeadline';
import { renderSuperscript } from 'utils/superscriptUtils';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import CTA, { isValidCta } from 'components/shared/CTA/CTA';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { usePrefersReducedMotion } from 'hooks/usePrefersReducedMotion';
import type { TwoColumnVariantProps } from 'components/ContentBlock/types';

const MASK_CLASS = 'block overflow-hidden';

export function AnimatedTwoColumnVariant({ sys, eyebrow, headline, copy, cta, alignment }: TwoColumnVariantProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [revealReady, setRevealReady] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || prefersReducedMotion) return;
    setRevealReady(true);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setRevealed(true);
        observer.disconnect();
      },
      // Reveals once the section's top passes the middle of the viewport.
      { rootMargin: '0px 0px -50% 0px' },
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [prefersReducedMotion]);

  const lineClassName = `block transition-transform duration-[800ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none ${
    revealReady && !revealed && !prefersReducedMotion ? 'translate-y-[110%]' : ''
  }`;

  return (
    <div ref={sectionRef}>
      <div className="grid gap-12 md:grid-cols-2">
        <div className={alignment === 'Right' ? 'md:order-2' : undefined}>
          {eyebrow && (
            <div className={MASK_CLASS} {...liveUpdateAttrs(sys.id, 'eyebrow')}>
              <div className={`${lineClassName} font-display text-base font-medium tracking-[0.02em] text-fern`}>
                <RichTextHeadline headline={eyebrow} level="p" />
              </div>
            </div>
          )}
          <div className={`${MASK_CLASS} mt-6`} {...liveUpdateAttrs(sys.id, 'headline')}>
            <DisplayHeadline
              headline={headline}
              level="h2"
              lineClassName={lineClassName}
              className="font-sans text-[clamp(2.5rem,3.62vw,3.25rem)] font-light leading-[1.1] tracking-[-0.005em]"
            />
          </div>
        </div>
        <div className="font-sans text-lg font-light leading-[1.4]">
          {copy && (
            <div className="space-y-[25.2px]" {...liveUpdateAttrs(sys.id, 'copy')}>
              {copy.json.content.map((block, index) => (
                <div key={index} className={MASK_CLASS}>
                  <div className={lineClassName} style={{ transitionDelay: `${index * 100}ms` }}>
                    <RichTextCopy
                      copy={{ ...copy, json: { ...copy.json, content: [block] } }}
                      ySpacing="none"
                      additionalOptions={{
                        renderMark: {
                          [MARKS.BOLD]: children => <strong className="font-semibold text-fern">{children}</strong>,
                          [MARKS.SUPERSCRIPT]: renderSuperscript,
                        },
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          {isValidCta(cta) && (
            <div className="mt-6" {...liveUpdateAttrs(sys.id, 'cta')}>
              <CTA cta={cta} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
